import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Ban, Eye, GraduationCap, RefreshCw, Send, Users } from 'lucide-react';
import PageHead from '../../components/PageHead/PageHead';
import MetricCard from '../../components/MetricCard/MetricCard';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import TableCard from '../../components/TableCard/TableCard';
import Avatar from '../../components/Avatar/Avatar';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import LoadingState from '../../components/LoadingState/LoadingState';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import usePageTitle from '../../hooks/usePageTitle';
import { cancelReminderCampaign, getReminderCampaign } from '../../api/reminders';
import { useI18n } from '../../i18n/I18nContext';
import { errorText } from '../../utils/errorText';
import {
  formatDateTime, formatNumber, formatPercent, formatPhone, formatShortDateTime,
} from '../../utils/datetime';
import MessagePreview from './composer/MessagePreview';
import { CHANNELS, TASHKENT, conversionKind, conversionRate } from './reminderUi';
import { CHANNEL_ICONS } from './composer/ChannelPicker';
import { CAMPAIGN_STATUS_TONE, DELIVERY_STATUS_TONE, isInFlight } from './reminderView';
import { audienceText } from './HistoryTab';
import './Reminders.css';

const DELIVERY_STEP = 50;
const POLL_MS = 3000;
const MAX_POLLS = 20;

/**
 * One message (/reminders/:id): who it went to, the text, what each channel did and the
 * deliveries one by one (name, phone, channel, status, when it was sent, when the link was opened).
 * A message that is still SCHEDULED can be cancelled here; nothing is sent afterwards.
 */
export default function CampaignDetail() {
  const { campaignId } = useParams();
  const { t } = useI18n();
  const uid = useId();
  const [state, setState] = useState({ phase: 'loading', data: null, error: null });
  const [reloadKey, setReloadKey] = useState(0);
  const [shown, setShown] = useState(DELIVERY_STEP);
  const [askCancel, setAskCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const polls = useRef(0);

  const campaign = state.data;
  usePageTitle(campaign?.title || t('reminders.title'));

  useEffect(() => {
    let cancelled = false;
    getReminderCampaign(campaignId)
      .then((data) => { if (!cancelled) setState({ phase: 'ready', data, error: null }); })
      .catch((error) => { if (!cancelled) setState((prev) => ({ phase: 'error', data: prev.data, error })); });
    return () => { cancelled = true; };
  }, [campaignId, reloadKey]);

  // A message sent a moment ago is still going out: look again until it is done.
  const pending = isInFlight(campaign);
  useEffect(() => {
    if (!pending) {
      polls.current = 0;
      return undefined;
    }
    if (polls.current >= MAX_POLLS) return undefined;
    const timer = setTimeout(() => {
      polls.current += 1;
      setReloadKey((k) => k + 1);
    }, POLL_MS);
    return () => clearTimeout(timer);
  }, [pending, campaign]);

  const refresh = useCallback(() => {
    polls.current = 0;
    setReloadKey((k) => k + 1);
  }, []);

  const cancel = async () => {
    setCancelling(true);
    try {
      const updated = await cancelReminderCampaign(campaignId);
      setAskCancel(false);
      toast.success(t('reminders.detail.cancelled'));
      // The server answers with the campaign as it is now; read it again only when it did not.
      if (updated && updated.id) setState((prev) => ({ ...prev, data: { ...prev.data, ...updated } }));
      else refresh();
    } catch (err) {
      toast.error(errorText(err, t('reminders.detail.cancelError')));
      setAskCancel(false);
    } finally {
      setCancelling(false);
    }
  };

  const back = { to: '/reminders?tab=history' };

  if (!campaign && state.phase === 'loading') {
    return <div className="rem-page"><LoadingState skeleton="page" message={t('common.loading')} /></div>;
  }
  if (!campaign) {
    const code = state.error?.code;
    return (
      <div className="rem-page">
        <PageHead back={back} eyebrow={t('reminders.title')} title={t('reminders.title')} />
        {code === 404 ? (
          <PageEmptyState variant="search" title={t('reminders.detail.notFoundTitle')} description={t('reminders.detail.notFoundDesc')} />
        ) : code === 403 ? (
          <PageEmptyState variant="forbidden" />
        ) : (
          <PageEmptyState variant="error" title={t('reminders.detail.loadError')} onRetry={refresh} />
        )}
      </div>
    );
  }

  const stats = campaign.stats || {};
  const recipients = Number(campaign.recipients) || 0;
  const rate = conversionRate(recipients, stats.converted);
  const kind = conversionKind(campaign.audience);
  const deliveries = Array.isArray(campaign.deliveries) ? campaign.deliveries : [];
  const when = campaign.sentAt || campaign.scheduledAt || campaign.createdAt;
  const scheduled = campaign.status === 'SCHEDULED';
  const sub = [
    scheduled && campaign.scheduledAt
      ? t('reminders.detail.goesOut', { when: formatDateTime(campaign.scheduledAt, { timeZone: TASHKENT }) })
      : formatDateTime(when, { timeZone: TASHKENT }),
    campaign.createdBy?.name ? t('reminders.history.by', { name: campaign.createdBy.name }) : '',
  ].filter(Boolean).join(' · ');

  const actions = (
    <>
      <button type="button" className="ds-btn ds-btn-secondary" onClick={refresh}>
        <RefreshCw aria-hidden="true" />
        {t('reminders.history.refresh')}
      </button>
      {scheduled && (
        <button type="button" className="ds-btn ds-btn-danger" onClick={() => setAskCancel(true)}>
          <Ban aria-hidden="true" />
          {t('reminders.detail.cancel')}
        </button>
      )}
    </>
  );

  const kpis = [
    { key: 'recipients', title: t('reminders.history.col.recipients'), value: formatNumber(recipients), icon: <Users />, tone: 'brand' },
    { key: 'sent', title: t('reminders.history.col.sent'), value: formatNumber(stats.sent || 0), change: stats.failed ? t('reminders.detail.failedHint', { count: stats.failed }) : undefined, icon: <Send />, tone: 'info' },
    { key: 'opened', title: t('reminders.history.col.opened'), value: formatNumber(stats.clicked || 0), icon: <Eye />, tone: 'purple' },
    {
      key: 'converted',
      title: t(`reminders.detail.converted.${kind}`),
      value: formatNumber(stats.converted || 0),
      change: t('reminders.detail.rate', { percent: formatPercent(rate) }),
      icon: <GraduationCap />,
      tone: 'success',
    },
  ];

  return (
    <div className="rem-page">
      <PageHead
        back={back}
        eyebrow={t('reminders.title')}
        title={campaign.title}
        description={sub}
        actions={actions}
      />

      <div className="kpi-row">
        {kpis.map(({ key, ...kpi }) => <MetricCard key={key} {...kpi} countUp={false} />)}
      </div>

      <div className="page-grid page-grid--rail">
        <div className="ui-stack">
          <section className="section-card" aria-labelledby={`${uid}-channels`}>
            <div className="section-head">
              <h2 className="section-head__title" id={`${uid}-channels`}>{t('reminders.detail.byChannel')}</h2>
            </div>
            <ul className="rem-bychannel">
              {CHANNELS.filter((key) => (campaign.channels || []).includes(key)).map((key) => {
                const Icon = CHANNEL_ICONS[key];
                const counts = campaign.byChannel?.[key] || {};
                return (
                  <li key={key} className="rem-bychannel__row">
                    <span className="rem-bychannel__name">
                      <span className="rmc-channel__icon" aria-hidden="true"><Icon /></span>
                      {t(`reminders.channel.${key}`)}
                    </span>
                    <span className="rem-bychannel__counts">
                      <span>{t('reminders.delivery.SENT')} <strong>{formatNumber(counts.sent || 0)}</strong></span>
                      <span>{t('reminders.delivery.FAILED')} <strong>{formatNumber(counts.failed || 0)}</strong></span>
                      <span>{t('reminders.delivery.SKIPPED')} <strong>{formatNumber(counts.skipped || 0)}</strong></span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="rem-deliveries" aria-labelledby={`${uid}-deliveries`}>
            <div className="section-head">
              <h2 className="section-head__title" id={`${uid}-deliveries`}>{t('reminders.detail.deliveries')}</h2>
              <span className="ui-count">{deliveries.length}</span>
            </div>
            {deliveries.length === 0 ? (
              <InlineEmpty
                kind="empty"
                title={scheduled ? t('reminders.detail.noDeliveriesYet') : t('reminders.detail.noDeliveries')}
              />
            ) : (
              <>
                <TableCard aria-label={t('reminders.detail.deliveries')}>
                  <table className="ui-table ui-table--stack">
                    <thead>
                      <tr>
                        <th>{t('reminders.detail.col.person')}</th>
                        <th>{t('reminders.detail.col.channel')}</th>
                        <th>{t('reminders.detail.col.status')}</th>
                        <th>{t('reminders.detail.col.sentAt')}</th>
                        <th>{t('reminders.detail.col.openedAt')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliveries.slice(0, shown).map((delivery) => (
                        <tr key={delivery.id || `${delivery.user?.id}-${delivery.channel}`}>
                          <td>
                            <div className="ui-cell-person">
                              <Avatar name={delivery.user?.name} id={delivery.user?.id} decorative />
                              <div className="ui-cell-person__text">
                                <span className="ui-cell-person__name">{delivery.user?.name || '—'}</span>
                                <span className="ui-cell-person__sub">{formatPhone(delivery.user?.phone) || '—'}</span>
                              </div>
                            </div>
                          </td>
                          <td data-label={t('reminders.detail.col.channel')}>{t(`reminders.channel.${delivery.channel}`)}</td>
                          <td data-label={t('reminders.detail.col.status')}>
                            <StatusBadge tone={DELIVERY_STATUS_TONE[delivery.status] || 'neutral'}>
                              {t(`reminders.delivery.${delivery.status}`)}
                            </StatusBadge>
                          </td>
                          <td data-label={t('reminders.detail.col.sentAt')} className="rem-nowrap">
                            {delivery.sentAt ? formatShortDateTime(delivery.sentAt, { timeZone: TASHKENT }) : '—'}
                          </td>
                          <td data-label={t('reminders.detail.col.openedAt')} className="rem-nowrap">
                            {delivery.clickedAt ? formatShortDateTime(delivery.clickedAt, { timeZone: TASHKENT }) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableCard>
                {deliveries.length > shown && (
                  <button type="button" className="ds-btn ds-btn-secondary rem-more" onClick={() => setShown((n) => n + DELIVERY_STEP)}>
                    {t('reminders.detail.showMore', { count: Math.min(DELIVERY_STEP, deliveries.length - shown) })}
                  </button>
                )}
              </>
            )}
          </section>
        </div>

        <aside className="col-rail" aria-label={t('reminders.message.title')}>
          <section className="section-card" aria-labelledby={`${uid}-message`}>
            <div className="section-head">
              <h2 className="section-head__title" id={`${uid}-message`}>{t('reminders.message.title')}</h2>
              <StatusBadge tone={CAMPAIGN_STATUS_TONE[campaign.status] || 'neutral'}>
                {t(`reminders.status.${campaign.status}`)}
              </StatusBadge>
            </div>
            <MessagePreview template={campaign.message} values={{}} promoCode={campaign.discount?.code} />
            <dl className="ui-kv rem-detail-facts">
              <div className="ui-kv__row">
                <dt>{t('reminders.audience.title')}</dt>
                <dd>{audienceText(t, campaign)}</dd>
              </div>
              {campaign.linkCourse && (
                <div className="ui-kv__row">
                  <dt>{t('reminders.link.course')}</dt>
                  <dd>{campaign.linkCourse.title || '—'}</dd>
                </div>
              )}
              {campaign.discount && (
                <div className="ui-kv__row">
                  <dt>{t('reminders.link.promo')}</dt>
                  <dd>{campaign.discount.code}</dd>
                </div>
              )}
              {(campaign.channels || []).includes('SMS') && (
                <div className="ui-kv__row">
                  <dt>{t('reminders.channel.SMS')}</dt>
                  <dd>{t(campaign.smsFallbackOnly ? 'reminders.detail.smsFallback' : 'reminders.detail.smsAlways')}</dd>
                </div>
              )}
            </dl>
          </section>
        </aside>
      </div>

      <ConfirmDialog
        open={askCancel}
        title={t('reminders.detail.cancelTitle')}
        description={t('reminders.detail.cancelDesc')}
        confirmLabel={t('reminders.detail.cancelConfirm')}
        cancelLabel={t('reminders.detail.keep')}
        tone="danger"
        loading={cancelling}
        onConfirm={cancel}
        onCancel={() => setAskCancel(false)}
      />
    </div>
  );
}
